import {
  computed,
  defineComponent,
  onBeforeMount,
  onUnmounted,
  reactive,
  watch,
} from "vue";
import { ref, onMounted, nextTick } from "vue";
import {
  currentEstTime,
  formatTime,
  GetBool,
  getRandomNumber,
  IsNullorWhiteSpace,
  isValidJSONString,
} from "@/utilities/utilities";
import ChatWootClientApiServices from "@/assets/services/chatwoot/clientApiServices";
import FlowiseAIApiServices from "@/assets/services/flowiseAI/apiServices";
import { storeToRefs } from "pinia";
import { useChatMessageInfo } from "@/store/chatInfo.store";
import {
  SenderType,
  Status,
  WebSocketUpdateMessage,
  WidgetList,
} from "@/constants/common-constants";
import { useEnvConfigStore } from "@/store/env.store";
import { SpanSupportApiServices } from "@/assets/services/spanSupport/apiServices";
import {
  Agents,
  ExtractDocument,
  GetConversationBySession,
  GetSummaryPdf,
  GetUserIdFromJWT,
  SaveChatConversations,
  SendMessageToLiveAgent,
  ToggleLiveChatStatus,
  UpdateActiveAI,
  UpdateMessageCommand,
} from "@/constants/url-constants";
import axios from "axios";
import {
  useSpeechSynthesis,
  useClipboard,
  useSpeechRecognition,
  useThrottleFn,
  usePermission,
  useDropZone,
  useObjectUrl,
} from "@vueuse/core";
import { uuid } from "vue-uuid";
import { useGlobalStore } from "@/store/globalStore";
import { Picker, EmojiIndex } from "emoji-mart-vue-fast/src";
import {
  Camera,
  LucideSend,
  LucideBot,
  LucideHeadphones,
  User,
  Receipt,
} from "lucide-vue-next";
import ChatWootApplicationApiServices from "@/assets/services/chatwoot/applicationApiServices";
import { EventBus } from "@/assets/services/eventbus";
import BotBatchIcon from "../../assets/images/bot-badge-icon.svg";
import { FlowiseClient } from "flowise-sdk";
import moment from "moment-timezone";
import html2pdf from "html2pdf.js";
// import { jsPDF } from "jspdf";
import pkg from "file-saver";
import { FormType, TaxYear, TINTypes } from "@/constants/dropdown";
import useVuelidate from "@vuelidate/core";
import { helpers, maxLength, required } from "@vuelidate/validators";
import { Rules } from "@/constants/validations";
import { SummaryFormPdf } from "@/models/Request/SummaryFormPdf";
import emojiData from "emoji-mart-vue-fast/data/all.json";
import { AlertDetail } from "@/models/Base/AlertDetail";
import { useUserInfo } from "@/store/userInfo.store";
import { HomeSiteInitialQuestion, SQLAgentInitialQuestion } from "@/constants/content";
import { ReturnStatus } from "@/models/Request/ReturnStatus";
import type { methodDialog } from "@/constants/interfaces";

export default defineComponent({
  name: "AIChatBot",
  components: {
    Picker,
    Camera,
    LucideSend,
    LucideHeadphones,
    LucideBot,
  },
  async setup(props: any, ctx) {
    const chatMessage = ref<string>("");
    const isWaitingLoader = ref<boolean>(false);
    const isChatBotDialog = ref<boolean>(false);
    const chatWootClientApiServices = reactive(new ChatWootClientApiServices());
    const flowiseAIApiServices = reactive(new FlowiseAIApiServices());
    const socket = ref();
    const isLiveAgent = ref<boolean>(false);
    const messageReceivedCount = ref<number>(0);
    const isChangeToFlowiseAI = ref<boolean>(true);
    const {
      ChatMessageDetails,
      LiveAgentChatMessageDetails,
      ChatNewSessionId,
      ChatOldSessionId,
      ChatCallFrom,
      ChatLiveAgentDetails,
      ConversationDetails,
      ContactDetails,
      DomSnapBase64Image,
      isSocketConnected,
    } = storeToRefs(useChatMessageInfo());
    const { ClearChatDetails, socketSend } = useChatMessageInfo();
    const envConfigStore = useEnvConfigStore();
    const { aiChatBotKeys, envInfo } = storeToRefs(envConfigStore);
    const spanSupportApiServices = reactive(new SpanSupportApiServices());
    const stepperElement = ref<number>(0);
    const isEnableLiveAgent = ref<boolean | null>(
      GetBool(aiChatBotKeys.value?.IsLiveAgent)
    );
    const isAIChatMessageSentToLiveAgent = ref<boolean>(false);
    const sessionId = ref<string>("");
    let oldChatConversationDetails = ref<{}[]>([]);
    let isAllowTypeFormatter = ref<boolean>(false);
    const textToSpeak = ref<string>("");
    const {
      isSupported,
      isPlaying,
      status,
      utterance,
      error,
      stop,
      toggle,
      speak,
    } = useSpeechSynthesis(textToSpeak.value, {
      lang: "en-US",
      pitch: 1,
      rate: 1,
      volume: 1,
    });
    const source = ref<string>("");
    const {
      isSupported: isClipBoardSupported,
      text,
      copy,
      copied,
    } = useClipboard({ source, legacy: true });
    const transcribedText = ref<string>("");
    const messageId = ref<string>("");
    const permissionWrite = usePermission("clipboard-write");
    let isTypingOn = ref<boolean>(false);
    const parentSessionId = ref<string | null>(null);
    const { saveAs } = pkg;
    const alphaNumAndDotValidator = Rules.alphaNumAndDotValidator;
    const {
      recognition,
      isSupported: isSpeechRecognitionSupported,
      isListening,
      result: spechRecognitionResult,
      start: startSpechRecognition,
      stop: stopSpechRecognition,
      error: spechRecognitionError,
    } = useSpeechRecognition({});
    const speakMessageId = ref<string>("");
    const isSendMessageAfterConversation = ref<boolean>(false);
    const globalStore = useGlobalStore();
    const dropZoneRef = ref<HTMLDivElement>();
    let selectFiles = ref<{}[]>([]);
    let exclude = [
      "flags",
      "symbols",
      "recent",
      "foods",
      "objects",
      "activity",
      "nature",
      "places",
    ];
    let emojiIndex = new EmojiIndex(emojiData, { exclude });
    let isShowEmojiPicker = ref<boolean>(false);
    let emojiMartRef = ref<HTMLElement | null>(null);
    let chatBotContainer = ref<HTMLElement | null>(null);
    const fileInput = ref<HTMLElement>();
    const selectInputFiles = ref<File[]>([]);
    const previewFiles = ref<{}[]>([]);
    const form = new FormData();
    const {
      CustomMessage,
      IsLiveChatConversationCreated,
      createConversation,
      UserDetails,
      UserMetaDataDetails,
      IsCustomMessageAnswerReceived,
      ChatCommandCommunication,
      createContact,
      NotificationCount,
      BotIconPath,
      isGetLatestLiveChat,
      IsStartNewChat,
      IsLiveChatContactCreated,
      TotalNotificationCount,
      CurrentSpanBotUSPSAccessKey,
      CurrentSpanBotAccessKey,
      CurrentSpanBotReturnStatusAccessKey,
      CurrentSpanBotYoutubeAccessKey,
      CurrentSpanBotUrl,
    } = useGlobalStore();
    const isTypingCompleted = ref<boolean>(true);
    const buttonMode = ref<null | number>(0);
    const chatWootApplicationApiServices = reactive(
      new ChatWootApplicationApiServices()
    );
    const liveAgentList = ref<{}[]>([]);
    const isLiveAgentAvailableOnline = ref<boolean>(false);
    const isDisableSwitchIcon = ref<boolean>(false);
    const isLastLiveChatResolved = ref<boolean>(false);
    const isLiveAgentTyping = ref<boolean>(false);
    const isPageUrlSended = ref<boolean>(false);

    const isTaxBandits = computed(() => {
      return aiChatBotKeys.value?.ProductName?.toLowerCase()?.includes(
        "taxbandits"
      );
    });

    const isTax990 = computed(() => {
      return aiChatBotKeys.value?.ProductName?.toLowerCase()?.includes("tax990");
    });

    const welcomeHtml = computed(()=>{
      if(aiChatBotKeys.value?.ProductName?.toLowerCase()?.includes("taxbandits") || aiChatBotKeys.value?.ProductName?.toLowerCase()?.includes("tax990"))
      {
        return `<div class="bot-content">Hi, I’m your AI Assistant,
          here to make things easier with instant answers to your queries about our product and services.
          </div>`
      }
      else
      {
        return  `<div class="bot-container">
        <div class="bot-header">Hi there, welcome to Bot 👋</div>
        <div class="bot-content">
          You are now speaking with <span style="font-weight: bold;" class='text-primary'>${
            aiChatBotKeys.value?.ProductName?.toLowerCase()?.includes("taxbandits")
              ? "TaxBandits"
              : capitalizeFirstWord(UserDetails.value?.Product)
          }</span> AI Agent. 
          I can do much more than chatbots you've seen before. Tell me as much as you can about your question, 
          and I'll do my best to help you in an instant.
        </div>
      </div>`
      }
    });

    const latestLiveChatConversation = ref<Array<{}>>([]);
    let flowiseClient = ref();
    const isStreamingProcessing = ref<boolean>(false);
    const isWantProcessCustomMessage = ref<boolean>(false);
    const excelDocument = ref<[]>([]);
    const isUploadButtonLoading = ref<boolean>(false);
    let oldAIChatConversationDetails = ref<{}[]>([]);
    let oldLiveChatConversationDetails = ref<{}[]>([]);
    const isSendButtonLoader = ref<boolean>(false);
    const downloadRefs = ref([]);
    const summaryFormPdf = ref(new SummaryFormPdf());
    const isGeneratePdfButtonLoader = ref<boolean>(false);
    const isShowAlertBox = ref<boolean>(false);
    const alertDetail = reactive(new AlertDetail());
    const userID = ref<string>("");
    const { getUserID, setUserID } = useUserInfo();
    const isUSPSAddressButtonLoader = ref<boolean>(false);
    const uspsAddress = ref<string>("");
    let isMaximizeWidget = ref<boolean>(false);
    const isEnableBot2 = ref<boolean>(false);
    const isShowInitialQuestions = ref<boolean>(true);
    const followUpPrompts = ref<string[]>([]);
    const initialPromptsQuestion = ref<string[]>([]);
    const isUpdateSecondaryAI = ref<boolean>(false);
    const timeOut = ref();
    const isFlowiseAPIResponseReceived = ref<boolean>(false);
    const returnStatus = reactive(new ReturnStatus());
    const isCheckReturnStatusButtonLoader = ref<boolean>(false);
    const userLoginRef = ref<methodDialog | null>(null);
    const isOpenLogin = ref<boolean>(false);
    const isAIChatEnabled = ref<boolean | null>(
      GetBool(aiChatBotKeys.value?.IsAIChat)
    );

    onBeforeMount(async () => {
      // socket.value = new WebSocket(aiChatBotKeys.value?.WebSocketUrl ?? "");
      // getLiveAgentAvailableDetails()
      isLiveAgent.value = !isAIChatEnabled.value
      if (
        !IsNullorWhiteSpace(ConversationDetails.value) &&
        Object.keys(ConversationDetails.value ?? {})?.length != 0 &&
        !isGetLatestLiveChat.value
      ) {
        GetConversationBasedConversationId();
        isGetLatestLiveChat.value = true;
      }
      flowiseClient.value = new FlowiseClient({
        baseUrl: CurrentSpanBotUrl.value?.slice(0, -1),
      });
      if (
        (IsNullorWhiteSpace(ContactDetails.value) ||
          Object.keys(ContactDetails.value)?.length == 0 ||
          ContactDetails.value?.email != UserDetails.value?.EmailAddress) &&
        !IsNullorWhiteSpace(UserDetails.value?.EmailAddress) &&
        aiChatBotKeys.value?.IsLiveAgent
      ) {
        createContact();
      } else {
        initializeWebSocket();
      }
      if (
        !IsNullorWhiteSpace(ConversationDetails.value) &&
        Object.keys(ConversationDetails.value ?? {})?.length != 0
      ) {
        IsLiveChatConversationCreated.value = true;
      }
      //  window.open('https://tbsnew.spanplan.net/volume_based_calculator')
      // open('https://tbsnew.spanplan.net/volume_based_calculator', 'Pricing Calculator')

      //Get UserId
      if (!IsNullorWhiteSpace(getUserID)) {
        userID.value = getUserID;
      } else if (!IsNullorWhiteSpace(UserDetails.value?.JwtToken)) {
        getUserIdFromJWT();
      }
      initialPromptsQuestion.value =
        isAIChatEmpty.value &&
        aiChatBotKeys.value?.FeatureFlags?.IsShowInitialPromptQuestions || aiChatBotKeys.value?.FeatureFlags?.IsEnableSQLAgent
          ? UserDetails.value?.IsHomeSite
            ? HomeSiteInitialQuestion
            : SQLAgentInitialQuestion
          : [];
    });

    // watch(isTypingOn,()=>{
    // sendMessageTypingIndicator(isTypingOn.value)
    // })

    watch(isLiveAgent, async () => {
      if (isLiveAgent.value) {
        NotificationCount.value ? showLiveAgentMessage() : "";
        // !isAIChatMessageSentToLiveAgent.value?
        IsLiveChatConversationCreated.value
          ? sendMessageToLiveAgent("SendAIChat")
          : (isSendMessageAfterConversation.value = true);
        // : "";
        stepperElement.value = 1;
        buttonMode.value = 1;
        if (
          IsNullorWhiteSpace(ContactDetails.value) ||
          Object.keys(ContactDetails.value)?.length == 0 ||
          ContactDetails.value?.email != UserDetails.value?.EmailAddress
        ) {
          createContact();
        }
      } else {
        buttonMode.value = 0;
        stepperElement.value = 0;
      }
      isWaitingLoader.value = false;
    });

    watch(CustomMessage, () => {
      if (
        CustomMessage.value.type == "OpenAIChat" &&
        !IsNullorWhiteSpace(CustomMessage.value.message)
      ) {
        if (isWaitingLoader.value) {
          isWantProcessCustomMessage.value = true;
        } else {
          customMessageProcess();
        }
      }
    });

    watch(stepperElement, () => {
      setTimeout(() => {
        scrollToBottom();
      }, 300);
    });

    watch(IsLiveChatConversationCreated, () => {
      if (IsLiveChatConversationCreated.value) {
        isSendButtonLoader.value = false;
        if (isSendMessageAfterConversation.value) {
          sendMessageToLiveAgent("SendAIChat");
        }
        if (chatMessage.value) {
          throttledClick();
        }
      }
    });

    watch(
      () => [IsLiveChatContactCreated.value, isSocketConnected.value],
      () => {
        if (IsLiveChatContactCreated.value && isSocketConnected.value) {
          initializeWebSocket();
          IsLiveChatContactCreated.value = false;
        }
      }
    );

    watch(isWaitingLoader, () => {
      isDisableSwitchIcon.value = isWaitingLoader.value;
      if (isWantProcessCustomMessage.value) {
        isWantProcessCustomMessage.value = false;
        customMessageProcess();
      }
    });

    onMounted(async () => {
      if (
        CustomMessage.value.type == "OpenAIChat" &&
        !IsNullorWhiteSpace(CustomMessage.value.message)
      ) {
        if (isWaitingLoader.value) {
          isWantProcessCustomMessage.value = true;
        } else {
          customMessageProcess();
        }
      }
      if (IsStartNewChat.value) {
        newChat();
        IsStartNewChat.value = false;
      }

      scrollToBottom();
      sessionId.value =
        ChatCallFrom.value == "NewChat"
          ? ChatNewSessionId.value
          : ChatOldSessionId.value;

      if (
        ChatCallFrom.value == "OldChat" ||
        (ChatCallFrom.value == "OldChatFromHome" && isAIChatEmpty.value)
      ) {
        getConversationBySession();
        ChatMessageDetails.value = [];
        LiveAgentChatMessageDetails.value = [];
      } else {
        if (ChatMessageDetails.value.length == 0) {
          ChatMessageDetails.value.push({
            createdAt: currentEstTime.value,
            text: welcomeHtml.value,
            senderType: SenderType.Bot,
            receiverType: SenderType.Contact,
            messageId: uuid.v4(),
            isWelcomeMessage: true,
          });
          isAllowTypeFormatter.value = false;
        }
      }

      EventBus.on("message", async (message: any) => {
        const data = message;
        if (data?.message?.event === WebSocketUpdateMessage.MessageCreated) {
          isWaitingLoader.value = false;
          const messageData = data.message.data;

          if (data.message.data.sender_type != "Contact") {
            TotalNotificationCount.value++;
            ChatCommandCommunication({
              command: "Notification",
              count: TotalNotificationCount.value,
            });
            messageReceivedCount.value += 1;
            ChatLiveAgentDetails.value.AvailableName =
              messageData?.performer?.available_name;
            ChatLiveAgentDetails.value.Avatarurl =
              messageData?.performer?.avatar_url;
            const lastConversation = LiveAgentChatMessageDetails.value.findLast(
              (item) =>
                item.senderType == SenderType.LiveAgent &&
                item.color != "Yellow"
            );
            if (
              lastConversation?.liveAgentName !=
                ChatLiveAgentDetails.value.AvailableName ||
              (IsNullorWhiteSpace(lastConversation) &&
                !!ChatLiveAgentDetails.value.AvailableName)
            ) {
              LiveAgentChatMessageDetails.value.push({
                createdAt: currentEstTime.value,
                text: `Hi this is ${ChatLiveAgentDetails.value.AvailableName}`,
                senderType: SenderType.LiveAgent,
                receiverType: SenderType.Contact,
                messageId: uuid.v4(),
                liveAgentAvatar: ChatLiveAgentDetails.value.Avatarurl,
                liveAgentName: ChatLiveAgentDetails.value.AvailableName,
              });
            }
            if (isLiveAgent.value) {
              const attachments = messageData.attachments?.map((image) => {
                return {
                  FileType: image.file_type,
                  FileSize: image.file_size,
                  DataUrl: image.data_url,
                };
              });

              LiveAgentChatMessageDetails.value.push({
                createdAt: currentEstTime.value,
                text: messageData.content,
                senderType: SenderType.LiveAgent,
                receiverType: SenderType.Contact,
                attachments: attachments,
                messageId: uuid.v4(),
                liveAgentAvatar: ChatLiveAgentDetails.value.Avatarurl,
                liveAgentName: ChatLiveAgentDetails.value.AvailableName,
              });

              scrollToBottom();
            } else {
              LiveAgentChatMessageDetails.value.push({
                createdAt: currentEstTime.value,
                text: messageData.content,
                senderType: SenderType.LiveAgent,
                receiverType: SenderType.Contact,
                attachments: messageData.attachments,
                messageId: uuid.v4(),
                liveAgentAvatar: ChatLiveAgentDetails.value.Avatarurl,
                liveAgentName: ChatLiveAgentDetails.value.AvailableName,
              });
              NotificationCount.value++;
            }
            latestLiveChatConversation.value = [];
            const lastChat = LiveAgentChatMessageDetails.value.findLast(
              (chat) => chat.senderType == SenderType.Contact
            );
            latestLiveChatConversation.value.push(lastChat);
            latestLiveChatConversation.value.push(
              LiveAgentChatMessageDetails.value[
                LiveAgentChatMessageDetails.value.length - 1
              ]
            );

            saveChatConversation("LatestLiveChat");
          }
        } else if (
          isLiveAgent.value &&
          data?.message?.event == WebSocketUpdateMessage.StatusChanged
        ) {
          IsLiveChatConversationCreated.value = false;
          messageReceivedCount.value = 0;
          LiveAgentChatMessageDetails.value.push({
            createdAt: currentEstTime.value,
            text: "The above conversation is resolved",
            senderType: SenderType.LiveAgent,
            receiverType: SenderType.Contact,
            color: "Yellow",
          });
          isLastLiveChatResolved.value = true;
          ConversationDetails.value = {};
        } else if (
          isLiveAgent.value &&
          data?.message?.event == WebSocketUpdateMessage.conversationtyping_on
        ) {
          isLiveAgentTyping.value = true;
          const lastConversation = LiveAgentChatMessageDetails.value.findLast(
            (item) =>
              item.senderType == SenderType.LiveAgent && item.color != "Yellow"
          );
          if (
            lastConversation?.liveAgentName !=
              ChatLiveAgentDetails.value.AvailableName ||
            (IsNullorWhiteSpace(lastConversation) &&
              !!ChatLiveAgentDetails.value.AvailableName)
          ) {
            LiveAgentChatMessageDetails.value.push({
              createdAt: currentEstTime.value,
              text: `Hi this is ${ChatLiveAgentDetails.value.AvailableName}, How can I help you?`,
              senderType: SenderType.LiveAgent,
              receiverType: SenderType.Contact,
              messageId: uuid.v4(),
              liveAgentAvatar: ChatLiveAgentDetails.value.Avatarurl,
              liveAgentName: ChatLiveAgentDetails.value.AvailableName,
            });
          }
        } else if (
          isLiveAgent.value &&
          data?.message?.event == WebSocketUpdateMessage.conversationtyping_off
        ) {
          isLiveAgentTyping.value = false;
        }
      });

      // socket.value.onopen = function () {
      //   if(IsLiveChatConversationCreated.value)
      //     {
      //       initializeWebSocket()
      //     }
      // };

      // socket.value.onmessage = async (message: any) => {
      // };
    });

    //Send message to FlowiseAI
    //   function sendMessageToFlowiseAI() {
    //     isWaitingLoader.value = true;
    //     isAllowTypeFormatter.value = true;
    //     ChatMessageDetails.value.push({
    //       createdAt:currentEstTime.value,
    //       text: chatMessage.value,
    //       senderType: SenderType.Contact,
    //       receiverType: SenderType.Bot,
    //     });
    //     scrollToBottom();

    //     const flowiseAIMessageRequest = {
    //       question: chatMessage.value,
    //       chatId: sessionId.value,
    //       overrideConfig: {
    //         analytics: {
    //           langFuse: {
    //             userId: UserDetails.value?.EmailAddress,
    //           },
    //         },
    //   }
    // }

    //     let question = chatMessage.value
    //     chatMessage.value = "";
    //     const url = `prediction/${aiChatBotKeys.value?.SpanBotAccessKey}`;
    //     flowiseAIApiServices
    //       .postMethod(url, flowiseAIMessageRequest)
    //       .then((res) => {
    //         isWaitingLoader.value = false;
    //         if (!IsNullorWhiteSpace(res) && res?.statusCode !=500 ) {
    //           const isIncludesLiveAgent = res?.text?.replace(" ", "").toUpperCase().includes("LIVEAGENT")
    //           isLiveAgent.value = isEnableLiveAgent.value
    //             ? isIncludesLiveAgent
    //             : false;
    //           if(!isEnableLiveAgent.value && UserDetails.value?.IsHomeSite && isIncludesLiveAgent)
    //           {
    //             res.text = ""
    //              ChatMessageDetails.value.push({
    //                 text: "Live Agent: Locate the orange pop-up at the bottom corner of the screen and click it to connect. The agent will assist you with your queries and provide real-time support.",
    //                 senderType: SenderType.Bot,
    //                 receiverType: SenderType.Contact,
    //                 createdAt:currentEstTime.value,
    //                 color: "Yellow",
    //               });
    //           }
    //           if (isLiveAgent.value) {
    //             !isAIChatMessageSentToLiveAgent.value
    //               ? sendMessageToLiveAgent("SendAIChat")
    //               : "";
    //             isWaitingLoader.value = true;
    //             setTimeout(() => {
    //               isWaitingLoader.value = false;
    //               LiveAgentChatMessageDetails.value = LiveAgentChatMessageDetails.value.filter((data)=>!data.text?.includes("You are now connected with live agent."))

    //               // LiveAgentChatMessageDetails.value.push({
    //               //   text: "You are now connected with live agent.",
    //               //   senderType: SenderType.LiveAgent,
    //               //   receiverType: SenderType.Contact,
    //               //   createdAt:currentEstTime.value,
    //               //   color: "Yellow",
    //               // });
    //             }, 2000);
    //           } else if(!IsNullorWhiteSpace(res.text)) {
    //             isTypingCompleted.value = false
    //            let newMessageId = uuid.v4()
    //            const sourceDocuments = res?.sourceDocuments?.map((data)=>{
    //             return {
    //               Source:data?.metadata?.source,
    //               PageContent:data?.pageContent
    //             }
    //            })

    //            if(isIncludesLiveAgent && !isEnableLiveAgent.value)
    //            {
    //             ChatMessageDetails.value.push({
    //               text: "You can reach TaxBandits Support Team at +1 (704) 684-4751, Monday - Friday from 8:30 AM to 5:30 PM EST. They're also available via email at support@taxbandits.com and through live chat during business hours.",
    //               senderType: SenderType.Bot,
    //               receiverType: SenderType.Contact,
    //               createdAt:currentEstTime.value,
    //               messageId: newMessageId,
    //               color: "Yellow",
    //             });
    //            }
    //            else
    //            {
    //             ChatMessageDetails.value.push({
    //               text: res?.text,
    //               senderType: SenderType.Bot,
    //               receiverType: SenderType.Contact,
    //               createdAt:currentEstTime.value,
    //               messageId: newMessageId,
    //               sourceDocuments:sourceDocuments
    //             });
    //             let conversation = {
    //               Question:question,
    //               Answer:res?.text,
    //             }

    //           IsNullorWhiteSpace(aiChatBotKeys.value?.SpanBotYoutubeAccessKey) ?  saveChatConversation("AIChat",2)  : getYoutubeLink(conversation,newMessageId)
    //            }

    //           }

    //           scrollToBottom();
    //           if(!IsCustomMessageAnswerReceived.value){
    //             ChatCommandCommunication({ command: "ResponseReceived",text:res?.text });
    //           }
    //         } else {
    //           ChatMessageDetails.value.push({
    //             text: "Please try again.",
    //             senderType: SenderType.Bot,
    //             receiverType: SenderType.Contact,
    //             createdAt:currentEstTime.value,
    //             color: "Yellow",
    //           });
    //         }
    //       })
    //       .catch((e) => {});
    //   }

    //Not allow to submit while process the request(Only for Flowise AI chat)
    const isRestrictToClick = computed(() => {
      return (
        isSendButtonLoader.value ||
        (isLiveAgent.value
          ? IsNullorWhiteSpace(chatMessage.value) &&
            selectFiles.value.length == 0
          : IsNullorWhiteSpace(chatMessage.value) ||
            isWaitingLoader.value ||
            isStreamingProcessing.value ||
            isUploadButtonLoading.value ||
            isUSPSAddressButtonLoader.value)
      );
    });

    function openDialog() {
      isChatBotDialog.value = true;
      isLiveAgent.value = false;
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }

    function closeDialog(callFrom: string) {
      isChatBotDialog.value = false;
      if (callFrom == "Close") {
        (ChatMessageDetails.value = []),
          (LiveAgentChatMessageDetails.value = []);
      }
      ctx.emit("CloseAIBotDialog");
    }

    //Send message to chatwoot API
    async function sendMessageToLiveAgent(callFrom: string = "") {
      if (isLastLiveChatResolved.value) {
        LiveAgentChatMessageDetails.value = [];
        LiveAgentChatMessageDetails.value.push({
          createdAt: currentEstTime.value,
          text: `Old Conversation moved to History. <a href="javascript:void(0)" class="navigate-link">Click here</a>`,
          senderType: SenderType.LiveAgent,
          receiverType: SenderType.Contact,
          color: "Yellow",
        });
        isLastLiveChatResolved.value = false;
      }
      let input = chatMessage.value;
      let aiChat: any = [];
      if (callFrom != "SendAIChat") {
        isAllowTypeFormatter.value = true;
        LiveAgentChatMessageDetails.value.push({
          createdAt: currentEstTime.value,
          text: input,
          senderType: SenderType.Contact,
          receiverType: SenderType.LiveAgent,
          attachments: previewFiles.value,
        });
        scrollToBottom();
      } else {
        let index = ChatMessageDetails.value?.findLastIndex(
          (item) => item.IsAlreadyMessageSended
        );
        let conversations =
          index > 0
            ? ChatMessageDetails.value.slice(
                index + 1,
                ChatMessageDetails.value.length
              )
            : ChatMessageDetails.value;
        conversations = conversations.filter(
          (item) =>
            !item.isWelcomeMessage &&
            !item.text?.includes("You are now switched back to AI")
        );
        aiChat = conversations?.map((data) => {
          return `**${
            [data?.senderType] == "User"
              ? UserDetails.value?.ContactName
              : [data?.senderType]
          }**:\n ${data.text}\n${
            data?.youtubeLink ? `YouTubeLink: ${data?.youtubeLink}` : ""
          }\n`;
        });
        aiChat = aiChat.reduce((totalContent, data, index) => {
          return `${totalContent}${data}${data?.includes("Bot") ? "\n" : ""}`;
        }, "");
        if (ChatMessageDetails.value.length != 0) {
          ChatMessageDetails.value[
            ChatMessageDetails.value.length - 1
          ].IsAlreadyMessageSended = true;
        }
      }
      form.delete("attachments[]");
      form.delete("content");
      form.delete("message_type");
      form.delete("ConversationId");
      form.delete("ContactId");
      let chatInput = callFrom == "SendAIChat" ? aiChat : input;
      if (selectFiles.value) {
        for (const file of selectFiles.value) {
          if (file instanceof File) {
            form.append("attachments[]", file);
          }
        }
      }

      if (
        !isPageUrlSended.value &&
        !IsNullorWhiteSpace(UserDetails.value?.PageUrl) &&
        callFrom != "SendAIChat"
      ) {
        const pageName = UserDetails.value?.PageUrl.includes("?")
          ? UserDetails.value?.PageUrl.split("?")[0]
          : UserDetails.value?.PageUrl;
        const page = `**Page**: ${pageName}\n\n\n`;
        form.append("content", `${page}${chatInput}`);
        isPageUrlSended.value = true;
      } else {
        form.append("content", chatInput);
      }
      //  form.append('content_type', 'text');
      form.append("message_type", "incoming");
      form.append("ConversationId", ConversationDetails.value.id);
      form.append("ContactId", ContactDetails.value.source_id);

      if (callFrom != "SendAIChat") {
        chatMessage.value = "";
      }
      selectFiles.value = [];
      if (
        callFrom != "SendAIChat" ||
        (!IsNullorWhiteSpace(aiChat) && aiChat.length != 0)
      ) {
        await spanSupportApiServices
          .postMethod(SendMessageToLiveAgent, form)
          .then((res) => {
            if (!IsNullorWhiteSpace(res)) {
              scrollToBottom();
              selectFiles.value = [];
              previewFiles.value = [];
              if (callFrom == "SendAIChat") {
                isAIChatMessageSentToLiveAgent.value = true;
              } else {
                if (!IsNullorWhiteSpace(res.attachments)) {
                  LiveAgentChatMessageDetails.value[
                    LiveAgentChatMessageDetails.value.length - 1
                  ]?.attachments.forEach((item, index) => {
                    item.FileType = res.attachments[index].file_type;
                    item.FileSize = res.attachments[index].file_size;
                    item.DataUrl = res.attachments[index]?.data_url;
                  });
                }
                if (isAIChatEnabled.value) {
                  const timeOut = messageReceivedCount.value ? 600000 : 180000;
                  setTimeout(() => {
                    changeToFlowiseAI("timeout");
                  }, timeOut);
                }
                // isWaitingLoader.value = true;
              }
            }
          })
          .catch((err) => {
            // console.log(err);
          });
      }
    }

    //Initialize the websocket after create the pubsub token
    function initializeWebSocket() {
      setTimeout(() => {
        socketSend(
          JSON.stringify({
            command: "subscribe",
            identifier: JSON.stringify({
              channel: "RoomChannel",
              pubsub_token: ContactDetails.value.pubsub_token,
              account_id: aiChatBotKeys.value?.LiveChatAccountID,
              user_id: ContactDetails.value?.id,
            }),
          })
        );
        setInterval(() => {
          socketSend(
            JSON.stringify({
              command: "message",
              identifier: JSON.stringify({
                channel: "RoomChannel",
                pubsub_token: ContactDetails.value.pubsub_token,
                user_id: ContactDetails.value?.id,
              }),
              data: JSON.stringify({ action: "update_presence" }),
            })
          );
        }, 20000);
      }, 500);
    }

    function submit() {
      isTypingOn.value = false;
      if (isLiveAgent.value) {
        if (!IsNullorWhiteSpace(chatMessage.value) || selectFiles.value) {
          submitLiveChat();
        }
      } else {
        throttledClick();
      }
    }

    async function submitLiveChat() {
      const isCreateNewConversation =
        IsNullorWhiteSpace(ConversationDetails.value) ||
        Object.keys(ConversationDetails.value ?? {})?.length == 0;
      if (isCreateNewConversation) {
        await createConversation();
        isSendButtonLoader.value = true;
      } else {
        throttledClick();
      }
    }

    const throttledClick = useThrottleFn(() => {
      isLiveAgent.value ? sendMessageToLiveAgent() : flowiseStreaming();
    }, 1000);

    //Change the Flowise AI while Live agent is not responded
    function changeToFlowiseAI(callFrom: string = "") {
      let isChange =
        callFrom == "timeout"
          ? messageReceivedCount.value == 0
          : isLiveAgent.value;
      if (callFrom == "timeout" && messageReceivedCount.value > 0) {
        isDisableSwitchIcon.value = false;
      }
      if (isChange) {
        LiveAgentChatMessageDetails.value =
          LiveAgentChatMessageDetails.value.filter(
            (item) =>
              !item.text.includes(
                "Live agent is not available, Switching back to AI"
              )
          );
        LiveAgentChatMessageDetails.value.push({
          createdAt: currentEstTime.value,
          text: "Live agent is not available, Switching back to AI",
          senderType: SenderType.LiveAgent,
          receiverType: SenderType.Contact,
          color: "Yellow",
        });
        scrollToBottom();
        setTimeout(() => {
          isLiveAgent.value = false;
          buttonMode.value = 0;
        }, 2000);
        isWaitingLoader.value = false;
      }
    }

    // Scroll to view or bottom
    async function scrollToBottom() {
      await nextTick(() => {
        const chatBotContainer = document.getElementById("chatBotContainer");
        chatBotContainer?.scrollIntoView({ behavior: "smooth", block: "end" });
      });
    }

    function showLiveAgentMessage() {
      NotificationCount.value = 0;
      // if (!IsNullorWhiteSpace(liveAgentChatMessage.value)) {
      //   isLiveAgent.value = true;
      //   LiveAgentChatMessageDetails.value.push(liveAgentChatMessage.value);
      // }
    }

    function switchChatType() {
      isLiveAgent.value = !isLiveAgent.value;
    }

    function saveChatConversation(
      callFrom: string = "AIChat",
      count: number = 2
    ) {
      const chatMessageDetails = ChatMessageDetails.value.filter(
        (item) => item.color != "Yellow"
      );
      const liveAgentChatMessageDetails =
        LiveAgentChatMessageDetails.value.filter(
          (item) => item.color != "Yellow"
        );
      let quesAndAns: any = [];
      switch (callFrom) {
        case "AIChat":
          quesAndAns = chatMessageDetails.slice(-count);
          break;
        case "LiveChat":
          quesAndAns = liveAgentChatMessageDetails.slice(-2);
          break;
        case "LatestLiveChat":
          quesAndAns = latestLiveChatConversation.value;
          break;
      }
      // let quesAndAns =
      //   callFrom == "AIChat"
      //     ? chatMessageDetails.slice(-count)
      //     : liveAgentChatMessageDetails.slice(-2);
      const createdTime = moment
        .tz(quesAndAns[0].createdAt, "America/New_York")
        .format("YYYY-MM-DD HH:mm:ss.SSS z");
      let saveChatConversationRequest = {
        ConversationId: sessionId.value,
        UserEmailAddress:
          globalStore.UserDetails.value?.EmailAddress ||
          globalStore.UserMetaDataDetails.value?.IpAddress,
        SenderType: quesAndAns[0].senderType,
        ReceiverType: quesAndAns[0].receiverType,
        Title: quesAndAns[0]?.text,
        Description: quesAndAns[1]?.text,
        MessageType: "text",
        SessionId: sessionId.value,
        CreatedAt: createdTime,
        IsAIChat:
          quesAndAns[0].senderType != "LiveAgent" &&
          quesAndAns[0].receiverType != "LiveAgent",
        UserMetaData: UserMetaDataDetails.value,
        MessageId: quesAndAns[1]?.messageId || uuid.v4(),
        ParentSessionId: parentSessionId.value,
        Question: {
          Content: quesAndAns[0]?.text,
          Attachments: quesAndAns[0]?.attachments || [],
        },
        Answer: {
          Content: quesAndAns[1]?.text,
          Attachments: quesAndAns[1]?.attachments || [],
          YoutubeLink: quesAndAns[2]?.youtubeLink || "",
          LiveAgentName: quesAndAns[1]?.liveAgentName,
          LiveAgentAvatar: quesAndAns[1]?.liveAgentAvatar,
          SourceDocuments: quesAndAns[1]?.sourceDocuments,
        },
        Product: UserDetails.value?.Product,
        Page: UserDetails.value?.PageUrl,
        IsHomeSite: UserDetails.value?.IsHomeSite,
        ProductKey: UserDetails.value?.ProductKey,
        ProductId: UserDetails.value?.ProductId,
        PageId: String(UserDetails.value?.PageId),
      };
      spanSupportApiServices
        .postMethod(SaveChatConversations, saveChatConversationRequest)
        .then((res) => {
          if (
            !IsNullorWhiteSpace(res) &&
            res.OperationStatus == Status.Success
          ) {
            sessionId.value = res.SessionId;
            parentSessionId.value = res.ParentSessionId;
          }
        })
        .catch((e) => {});
    }

    function changeToAlternateChat() {
      isAllowTypeFormatter.value = false;
      isLiveAgent.value = !!buttonMode.value;
      if (buttonMode.value) {
        LiveAgentChatMessageDetails.value =
          LiveAgentChatMessageDetails.value.filter(
            (data) =>
              !data.text?.includes("You are now connected with live agent.")
          );
        //   LiveAgentChatMessageDetails.value.push({
        //   text: "You are now connected with live agent.",
        //   senderType: SenderType.LiveAgent,
        //   receiverType: SenderType.Contact,
        //   createdAt:currentEstTime.value,
        //   color: "Yellow",
        // });
      } else {
        ChatMessageDetails.value = ChatMessageDetails.value.filter(
          (data) => !data.text?.includes("You are now switched back to AI")
        );
        ChatMessageDetails.value.push({
          text: "You are now switched back to AI",
          senderType: SenderType.Bot,
          receiverType: SenderType.Contact,
          createdAt: currentEstTime.value,
          color: "Yellow",
        });
      }
    }

    //Get conversation details by session id
    function getConversationBySession() {
      spanSupportApiServices
        .getMethod(GetConversationBySession + "/" + sessionId.value)
        .then((res) => {
          if (!IsNullorWhiteSpace(res) && res.OperationStatus == "Success") {
            res.Messages?.forEach((chat) => {
              chat.IsAiChat
                ? oldAIChatConversationDetails.value.push(chat)
                : oldLiveChatConversationDetails.value.push(chat);
            });
            oldChatConversationDetails.value = res.Messages;
            isLiveAgent.value =
              oldChatConversationDetails.value[
                oldChatConversationDetails.value.length - 1
              ].SenderType == SenderType.LiveAgent ||
              oldChatConversationDetails.value[
                oldChatConversationDetails.value.length - 1
              ].ReceiverType == SenderType.LiveAgent;
            scrollToBottom();
          }
        })
        .catch((e) => {});
    }

    function readAloud(msg: string, msgId: string) {
      if (isSupported.value) {
        utterance.value.text = msg;
        speakMessageId.value = msgId;
        speak();
      }
    }

    async function copyMessage(message: string) {
      messageId.value = message?.messageId || message?.MessageId;
      source.value = message?.text ?? message?.Answer.Content;
      if (isClipBoardSupported.value) {
        await copy(source.value.trim())
          .then(() => {
            // console.log("Text copied successfully!");
          })
          .catch((error) => {
            console.error("Copy failed:", error);
          });
        // navigator.clipboard.writeText(source.value);
        // console.log(permissionWrite.value)
      }
    }

    const startListening = () => {
      if (isSpeechRecognitionSupported.value) {
        startSpechRecognition();
      }
    };

    // Stop listening
    const stopListening = () => {
      stopSpechRecognition();
    };

    function sendMessageTypingIndicator(isTypingOn: boolean) {
      const event = isTypingOn
        ? "conversation.typing_on"
        : "conversation.typing_off";
      const message = JSON.stringify({
        commend: "conversation",
        identifier: JSON.stringify({
          channel: "RoomChannel",
          pubsub_token: ContactDetails.value.pubsub_token,
          account_id: aiChatBotKeys.value?.LiveChatAccountID,
          user_id: ContactDetails.value.id,
        }),
        message: {
          event: event,
          data: {
            is_private: false,
            account_id: aiChatBotKeys.value?.LiveChatAccountID,
            conversation: {
              inbox_id: 3,
              id: ConversationDetails.value.id,
              contact_inbox: {
                contact_id: ContactDetails.value?.id,
                pubsub_token: ContactDetails.value.pubsub_token,
                source_id: ContactDetails.value.source_id,
                inbox_id: 3,
              },
              meta: {
                sender: {
                  email: UserDetails.value?.EmailAddress,
                  id: ContactDetails.value?.id,
                },
              },
            },
            user: {
              id: 2,
              type: "user",
            },
          },
        },
      });

      socketSend(message);
    }

    function thumbsCommends(
      commend: string,
      messageId: string,
      callFrom: string
    ) {
      const data =
        callFrom == "AIChat"
          ? ChatMessageDetails.value
          : callFrom == "OldChat"
          ? oldChatConversationDetails.value
          : LiveAgentChatMessageDetails.value;
      data.forEach((item) => {
        let itemMessageId =
          callFrom == "OldChat" ? item.MessageId : item.messageId;
        if (itemMessageId == messageId) {
          callFrom == "OldChat"
            ? (item.Commends = commend)
            : (item.commends = commend);
        }
      });
      const updateMessageCommandRequest = {
        SessionId: sessionId.value,
        Commends: commend,
        MessageId: messageId,
      };
      updateMessageCommand(updateMessageCommandRequest);
    }

    function updateMessageCommand(request: Record<string, string>) {
      spanSupportApiServices
        .postMethod(UpdateMessageCommand, request)
        .then((res) => {
          if (!IsNullorWhiteSpace(res) && res.OperationStatus == "Success") {
          }
        })
        .catch((e) => {});
    }

    async function onDrop(files: File[] | null) {
      if (isLiveAgent.value) {
        await processFiles(Array.from(files));
      }
    }
    const { isOverDropZone } = useDropZone(dropZoneRef, {
      onDrop,
      // specify the types of data to be received.
      // dataTypes: ['image/jpeg'],
      // control multi-file drop
      multiple: true,
      // whether to prevent default behavior for unhandled events
      preventDefaultForUnhandled: true,
    });

    const readFileAsDataURL = async (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = function () {
          resolve(reader.result);
        };
        reader.onerror = (err) => {
          reject(err);
        };
      });
    };

    const processFiles = async (files: File[]) => {
      files = files.length > 4 ? files.slice(0, 4) : files;
      if (selectFiles.value.length < 4) {
        let allowedImageTypes = [
          "image/jpeg",
          "image/png",
          "image/gif",
          "video/mp4",
          "application/pdf",
        ];
        let allowedFileList = files.filter((item) => {
          return (
            allowedImageTypes.includes(item.type) &&
            item.size <= 5 * 1024 * 1024 &&
            !isDuplicateImage(item)
          );
        });

        if (allowedFileList.length > 0) {
          for (const file of allowedFileList) {
            const imageUrl = useObjectUrl(file);
            const preview = {
              FileType: file.type.includes("image")
                ? "image"
                : file.type.includes("video")
                ? "video"
                : "file",
              FileSize: file.size,
              DataUrl: imageUrl.value,
              FileName: file.name,
            };
            previewFiles.value.push(preview);
            selectFiles.value.push(file);
          }
        }
      }
    };

    function isDuplicateImage(file: {}): Boolean {
      return selectFiles.value.some(
        (data) => data.name == file.name && data.size == file.size
      );
    }

    function handlePaste(data) {
      if (
        isLiveAgent.value &&
        data?.clipboardData?.types.includes("Files") &&
        !isDuplicateImage(data?.clipboardData?.files)
      ) {
        const files = data?.clipboardData?.files;
        processFiles(files);
      }
    }

    function showEmoji(emoji) {
      chatMessage.value = chatMessage.value + emoji.native;
    }

    // onClickOutside(emojiMartRef, (event) => {
    //   isShowEmojiPicker.value = false;
    // });

    function removeFile(index: number) {
      selectFiles.value.splice(index, 1);
      previewFiles.value.splice(index, 1);
    }

    function handleFileInputChange() {
      processFiles(selectInputFiles.value);
    }

    function getImageBlobUrl(file: File) {
      let imageUrl = URL.createObjectURL(file);
      // URL.revokeObjectURL(imageUrl)
      return imageUrl;
    }

    function getFileName(data: Record<string, string>) {
      if (!IsNullorWhiteSpace(data.FileName)) {
        return data.FileName;
      } else {
        let size = +data.FileSize;
        return size < 1024 * 1024
          ? `${(size / 1024).toFixed(2)}.kB`
          : `${((size / 1024) * 1024).toFixed(2)}.MB`;
      }
    }

    function showPreview(url: string) {
      window.open(url, "_blank");
    }

    //  async function getPdfPreviewUrl(url:URL){
    //   try{
    //     const loadingTask = pdfjsLib.getDocument(url);
    //     const pdf = await loadingTask.promise;
    //     const page = await pdf.getPage(1)
    //      const viewport = page.getViewport({ scale: 0.5 })
    //      const canvas = document.createElement('canvas')
    //      const context = canvas.getContext('2d')
    //      canvas.height = viewport.height
    //      canvas.width = viewport.width
    //      await page.render({
    //       canvasContext: context,
    //       viewport: viewport
    //     }).promise
    //     return canvas.toDataURL("image/png")
    //   }
    //   catch{
    //     return "";
    //   }
    //  }

    //Get Live agent available details
    function getLiveAgentAvailableDetails() {
      chatWootApplicationApiServices
        .getMethod(Agents)
        .then((res) => {
          if (!IsNullorWhiteSpace(res)) {
            liveAgentList.value = res;
            isLiveAgentAvailableOnline.value = liveAgentList.value.some(
              (data) => data?.availability_status == "online"
            );
          }
        })
        .catch((e) => {});
    }

    const filteredLiveAgentList = computed(() => {
      let agents = liveAgentList.value.filter(
        (agent) => !IsNullorWhiteSpace(agent.thumbnail)
      );
      if (
        liveAgentList.value.filter(
          (agent) =>
            IsNullorWhiteSpace(agent.thumbnail) &&
            IsNullorWhiteSpace(agent.name)
        )
      ) {
        agents.push({
          thumbnail:
            "https://avatars0.githubusercontent.com/u/9064066?v=4&s=460",
        });
      }
      return agents;
    });

    //Get Youtube link based on the conversation
    function getYoutubeLink(
      conversation: Record<string, string>,
      MessageId: string
    ) {
      isSendButtonLoader.value = true;
      const request = {
        chatId: sessionId.value,
        question: `Question:${conversation.Question}Answer:${conversation.Answer}`,
      };
      isAllowTypeFormatter.value = true;
      const url = `prediction/${CurrentSpanBotYoutubeAccessKey.value}`;
      flowiseAIApiServices
        .postMethod(url, request)
        .then(async (res) => {
          isSendButtonLoader.value = false;
          if (!IsNullorWhiteSpace(res)) {
            const text = res.text.replace(/[\s.]/g, "");
            if (!IsNullorWhiteSpace(res) && !text.includes("LinknotFound")) {
              // let index =  ChatMessageDetails.value.findIndex((message)=>message.messageId == MessageId)
              // ChatMessageDetails.value[index].youtubeLink= res.text
              let [Title, VideoURL] = res.text
                .split("\n")
                .map((line: String) => line.replace(/^(Title:|VideoURL:)/, ""));
                
              const isVideoAlreadyAvailable = ChatMessageDetails.value.some((data:Record<string,string>)=>{
                  return data?.youtubeLink ? data?.youtubeLink.includes(VideoURL) : false
              })
              if(!isVideoAlreadyAvailable)
               {
                let thumbnail = await getYouTubeThumbnail(VideoURL);
                ChatMessageDetails.value.push({
                  text: Title,
                  senderType: SenderType.Bot,
                  receiverType: SenderType.Contact,
                  createdAt: currentEstTime.value,
                  messageId:
                    ChatMessageDetails.value[ChatMessageDetails.value.length - 1]
                      .sessionId,
                  youtubeLink: VideoURL,
                  youtubeThumbnail: thumbnail,
                  isShowYoutubeReference: true,
                });
               }
               isVideoAlreadyAvailable ?   saveChatConversation("AIChat") :   saveChatConversation("AIChat", 3);
            } else {
              saveChatConversation("AIChat");
            }
          } else {
            saveChatConversation("AIChat");
          }
        })
        .catch((e) => {
          isSendButtonLoader.value = false
        })
        .finally(()=>{
          isSendButtonLoader.value = false   
        })
    }

    function getYouTubeThumbnail(link:string) {
      const videoId =
        link.match(
          /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/
        )?.[1] ||
        link.match(/(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^&]+)/)?.[1];
      return videoId
        ? `https://img.youtube.com/vi/${videoId}/0.jpg` // YouTube thumbnail URL
        : "default-thumbnail.jpg"; // Fallback thumbnail
    }

    function clearChat() {
      isWaitingLoader.value = false;
      isShowInitialQuestions.value = false;
      oldAIChatConversationDetails.value = [];
      followUpPrompts.value = [];
      ClearChatDetails();
    }

    function newChat() {
      isShowInitialQuestions.value = true;
      followUpPrompts.value = [];
      isWaitingLoader.value = false;
      ChatNewSessionId.value = uuid.v4();
      sessionId.value = ChatNewSessionId.value;
      parentSessionId.value = "";
      if (isLiveAgent.value) {
        LiveAgentChatMessageDetails.value = [];
        oldLiveChatConversationDetails.value = [];
      } else {
        ChatMessageDetails.value = [];
        oldAIChatConversationDetails.value = [];
      }

      if (ChatMessageDetails.value.length == 0) {
        ChatMessageDetails.value.push({
          createdAt: currentEstTime.value,
          text: welcomeHtml.value,
          senderType: SenderType.Bot,
          receiverType: SenderType.Contact,
          messageId: uuid.v4(),
          isWelcomeMessage: true,
        });
      }
    }

    onUnmounted(() => {
      EventBus.off("message");
    });

    function navigateToHistory() {
      ctx.emit("NavigateToOther", WidgetList.ChatHistory);
    }

    watch(
      () => LiveAgentChatMessageDetails.value,
      (newConversations) => {
        nextTick(() => {
          document.querySelectorAll(".navigate-link").forEach((link, index) => {
            link.addEventListener("click", () => navigateToHistory());
          });
        });
      },
      { deep: true }
    );

    function endChat() {
      if (LiveAgentChatMessageDetails.value.length > 0) {
        const endChatRequest = {
          Status: "resolved",
          ConversationId: ConversationDetails.value?.id,
        };
        spanSupportApiServices
          .postMethod(ToggleLiveChatStatus, endChatRequest)
          .then((res) => {
            if (!IsNullorWhiteSpace(res)) {
              IsLiveChatConversationCreated.value = false;
              ConversationDetails.value = {};
              isLastLiveChatResolved.value = true;
              LiveAgentChatMessageDetails.value.push({
                createdAt: currentEstTime.value,
                text: `Thank you for chatting with us. If you need further assistance, feel free to reach out again!`,
                senderType: SenderType.LiveAgent,
                receiverType: SenderType.Contact,
                color: "Yellow",
              });
            }
          })
          .catch((err) => {});
      }
    }

    async function GetConversationBasedConversationId() {
      latestLiveChatConversation.value = [];
      await chatWootClientApiServices
        .getMethod(
          `contacts/${ContactDetails.value?.source_id}/conversations/${ConversationDetails.value?.id}/messages`
        )
        .then((res) => {
          if (!IsNullorWhiteSpace(res)) {
            const latestConversation = res;
            const liveChatConversation =
              LiveAgentChatMessageDetails.value.filter(
                (chat) => chat.color != "Yellow"
              );
            const lastChat =
              liveChatConversation[liveChatConversation.length - 1];

            const index = latestConversation.findLastIndex((data) => {
              return data.content
                .replace(/\s+/g, "")
                .toLowerCase()
                .includes(lastChat?.text.replace(/\s+/g, "").toLowerCase());
            });
            if (index != -1) {
              latestLiveChatConversation.value.push(lastChat);
              const remainsConversations = latestConversation.slice(
                index + 1,
                latestConversation.length
              );
              let latestAllAttachments: any = [];
              let latestAllConversation = "";
              let sender_image = "";
              let sender_name = "";
              if (remainsConversations?.length > 0) {
                remainsConversations.forEach((data) => {
                  if (data?.sender?.type == "user") {
                    const attachments = data.attachments?.map((image) => {
                      return {
                        FileType: image.file_type,
                        FileSize: image.file_size,
                        DataUrl: image.data_url,
                      };
                    });
                    if (attachments) {
                      latestAllAttachments.push(attachments);
                    }
                    latestAllConversation = `${latestAllConversation} \n ${data?.content}`;
                    if (data?.sender?.avatar_url) {
                      sender_image = data?.sender?.avatar_url;
                    }
                    if (data?.sender?.available_name) {
                      sender_name = data?.sender?.name;
                    }
                    LiveAgentChatMessageDetails.value.push({
                      createdAt: currentEstTime.value,
                      text: data?.content,
                      senderType: SenderType.LiveAgent,
                      receiverType: SenderType.Contact,
                      attachments: attachments ?? "",
                      messageId: uuid.v4(),
                      liveAgentAvatar: data?.sender?.avatar_url,
                      liveAgentName: data?.sender?.name,
                    });
                  }
                });

                latestLiveChatConversation.value.push({
                  createdAt: currentEstTime.value,
                  text: latestAllConversation,
                  senderType: SenderType.LiveAgent,
                  receiverType: SenderType.Contact,
                  attachments: latestAllAttachments,
                  messageId: uuid.v4(),
                  liveAgentAvatar: sender_image,
                  liveAgentName: sender_name,
                });
                saveChatConversation("LatestLiveChat");
              }
            }
          }
        })
        .catch((err) => {});
    }

    //#region Flowise Api(PACKAGE)
    async function flowiseStreaming(callFrom: string = "") {
      followUpPrompts.value = [];
      isWaitingLoader.value = true;
      isStreamingProcessing.value = true;
      isAllowTypeFormatter.value = false;
      let chat = "";
      let question = "";
      if (aiChatBotKeys.value?.FeatureFlags?.IsEnableSQLAgent ) {
        chat = `${chatMessage.value}`;
        question = `UserID:${userID.value?.toUpperCase()},\n` + chat;
      } 
      else if (callFrom == "USPSValidator") {
        chat = "Address:\n\n" + uspsAddress.value;
        uspsAddress.value = "";
      } else {
        chat = chatMessage.value;
      }
      chatMessage.value = "";
      var chatflowId: string | undefined = "";
      ChatMessageDetails.value.push({
        createdAt: currentEstTime.value,
        text: chat,
        senderType: SenderType.Contact,
        receiverType: SenderType.Bot,
      });
      clearTimeout(timeOut.value);
      scrollToBottom();
      try {
        if (aiChatBotKeys.value?.FeatureFlags?.IsEnableAdminBot) {
          chatflowId = !isEnableBot2.value
            ? CurrentSpanBotAccessKey.value?.SpanBotAccessKey1
            : CurrentSpanBotAccessKey.value?.SpanBotAccessKey2;
        } else {
          switch (callFrom) {
            case "USPSValidator":
              chatflowId = CurrentSpanBotUSPSAccessKey.value;
              break;
            case "ReturnStatus":
              chatflowId = CurrentSpanBotReturnStatusAccessKey.value;
              break;
            default:
              chatflowId = CurrentSpanBotAccessKey.value;
              break;
          }
        }

        // setTimeout(() => {
        //   if(!isFlowiseAPIResponseReceived.value && aiChatBotKeys.value?.FeatureFlags?.IsEnableUpdateSecondaryAI)
        //   {
        //     updateSecondaryAI()
        //   }
        //   isFlowiseAPIResponseReceived.value = false
        // }, 30000);

        // For streaming prediction
        const request = callFrom == "ReturnStatus" || aiChatBotKeys.value?.FeatureFlags?.IsEnableSQLAgent ? question : chat;
        const prediction = await flowiseApi(chatflowId, request);
        for await (const chunk of prediction) {
          switch (chunk.event) {
            case "start":
              isFlowiseAPIResponseReceived.value = true;
              isWaitingLoader.value = false;
              let newMessageId = uuid.v4();
              ChatMessageDetails.value.push({
                text: "",
                senderType: SenderType.Bot,
                receiverType: SenderType.Contact,
                createdAt: currentEstTime.value,
                messageId: newMessageId,
                sourceDocuments: [],
              });
              switch (callFrom) {
                case "USPSValidator":
                  ChatMessageDetails.value = ChatMessageDetails.value.filter(
                    (chat: Record<string, string>) => !chat?.isUSPSValidator
                  );
                  isUSPSAddressButtonLoader.value = false;
                  break;
                case "ReturnStatus":
                  ChatMessageDetails.value = ChatMessageDetails.value.filter(
                    (chat: Record<string, string>) => !chat?.isCheckReturnStatus
                  );
                  isMaximizeWidget.value = true;
                  Object.assign(returnStatus, new ReturnStatus());
                  isCheckReturnStatusButtonLoader.value = false;
                  break;
              }
              break;
            case "token":
              ChatMessageDetails.value[
                ChatMessageDetails.value.length - 1
              ].text += chunk?.data;
              break;
            case "sourceDocuments":
              const sourceDocuments = chunk?.data?.map((data) => {
                return {
                  Source: data?.metadata?.source,
                  PageContent: data?.pageContent,
                };
              });
              ChatMessageDetails.value[
                ChatMessageDetails.value.length - 1
              ].sourceDocuments = sourceDocuments;
              break;
            case "metadata":
              if (chunk?.data?.followUpPrompts) {
                followUpPrompts.value = JSON.parse(
                  chunk?.data?.followUpPrompts
                );
              }
              break;
            case "end":
              isStreamingProcessing.value = false;
              handleFlowiseResponse(
                ChatMessageDetails.value[ChatMessageDetails.value.length - 1]
                  .text,
                callFrom
              );
              break;
            case "error":
              ChatMessageDetails.value.push({
                text: "Please try again.",
                senderType: SenderType.Bot,
                receiverType: SenderType.Contact,
                createdAt: currentEstTime.value,
                color: "Yellow",
              });
              isWaitingLoader.value = false;
              isUSPSAddressButtonLoader.value = false;
              isCheckReturnStatusButtonLoader.value = false;
              break;
          }
        }
      } catch (error) {
        isWaitingLoader.value = false;
        isCheckReturnStatusButtonLoader.value = false;
        console.error("Error:", error);
      }
    }
    //#endregion

    function handleFlowiseResponse(res: string, callFrom: string = "") {
      const isIncludesLiveAgent = res
        ?.replace(" ", "")
        .toUpperCase()
        .includes("LIVEAGENT");
      const isExcelToCSVConverter = res
        ?.replace(/\s/g, "")
        .toLocaleLowerCase()
        .includes("csvconverter");
      const isZIPToPdfConverter = res
        ?.replace(/\s/g, "")
        .toLocaleLowerCase()
        .includes("pdfconverter");
      const isSummaryPdfDownload = res
        ?.replace(/\s/g, "")
        .toLocaleLowerCase()
        .includes("downloadsummarypdf");
      const isUSPSAddressValidator = res
        ?.replace(/\s/g, "")
        .toLocaleLowerCase()
        .includes("uspsvalidator");
      const isReturnStatusChecker = res
        ?.replace(/\s/g, "")
        .toLocaleLowerCase()
        .includes("returnstatus");
      const isChangeLiveAgent = isEnableLiveAgent.value
        ? isIncludesLiveAgent
        : false;
      // if(callFrom == 'USPSValidator')
      //   {
      //    ChatMessageDetails.value[ChatMessageDetails.value.length-2].uspsValidator = true
      //   ChatMessageDetails.value[ChatMessageDetails.value.length-1].uspsValidator = true
      //   }
      if (
        !isEnableLiveAgent.value &&
        UserDetails.value?.IsHomeSite &&
        isIncludesLiveAgent
      ) {
        ChatMessageDetails.value.pop();
        ChatMessageDetails.value.push({
          text: "Live Agent: Locate the orange pop-up at the bottom corner of the screen and click it to connect. The agent will assist you with your queries and provide real-time support.",
          senderType: SenderType.Bot,
          receiverType: SenderType.Contact,
          createdAt: currentEstTime.value,
          color: "Yellow",
        });
      }
      if (isChangeLiveAgent) {
        ChatMessageDetails.value.pop();
        if (isEnableLiveAgent.value) {
          ChatMessageDetails.value = ChatMessageDetails.value.filter(
            (data) =>
              !data.text?.includes("Let me connect you to a live agent.")
          );
          ChatMessageDetails.value.push({
            text: "Let me connect you to a live agent.",
            senderType: SenderType.Bot,
            receiverType: SenderType.Contact,
            createdAt: currentEstTime.value,
            color: "Yellow",
          });
          setTimeout(() => {
            buttonMode.value = 1;
            checkEmailAddressAvailable();
            LiveAgentChatMessageDetails.value =
              LiveAgentChatMessageDetails.value.filter(
                (data) =>
                  !data.text?.includes("You are now connected with live agent.")
              );
          }, 2000);
        }
      } else if (isExcelToCSVConverter || isZIPToPdfConverter) {
        ChatMessageDetails.value.pop();
        const type = isZIPToPdfConverter ? "zip" : "xlsx";
        extractDocumentConverter(type);
      } else if (isSummaryPdfDownload) {
        ChatMessageDetails.value.pop();
        downloadSummaryPdf();
      } else if (isUSPSAddressValidator) {
        ChatMessageDetails.value.pop();
        enableValidateUSPSAddress();
      } else if (isReturnStatusChecker) {
        ChatMessageDetails.value.pop();
        enableCheckReturnStatus();
      } else if (!IsNullorWhiteSpace(res)) {
        isTypingCompleted.value = false;
        let newMessageId = uuid.v4();

        if (isIncludesLiveAgent && !isEnableLiveAgent.value) {
          ChatMessageDetails.value.pop();
          ChatMessageDetails.value.push({
            text: "You can reach TaxBandits Support Team at +1 (704) 684-4751, Monday - Friday from 8:30 AM to 5:30 PM EST. They're also available via email at support@taxbandits.com and through live chat during business hours.",
            senderType: SenderType.Bot,
            receiverType: SenderType.Contact,
            createdAt: currentEstTime.value,
            messageId: newMessageId,
            color: "Yellow",
          });
        } else {
          let conversation = {
            Question:
              ChatMessageDetails.value[ChatMessageDetails.value.length - 2]
                ?.text,
            Answer:
              ChatMessageDetails.value[ChatMessageDetails.value.length - 1]
                ?.text,
          };
          if (
            !IsNullorWhiteSpace(aiChatBotKeys.value?.SpanBotYoutubeAccessKey) &&
            !IsNullorWhiteSpace(conversation.Answer) &&
            callFrom != "USPSValidator" &&
            callFrom != "ReturnStatus"
          ) {
            getYoutubeLink(conversation, newMessageId);
          } else {
            saveChatConversation("AIChat", 2);
          }
        }
      }

      scrollToBottom();
      if (!IsCustomMessageAnswerReceived.value) {
        ChatCommandCommunication({
          command: "ResponseReceived",
          text: res?.text,
        });
      }
    }

    async function flowiseApi(
      chatflowId: string | undefined,
      chatMessage: string
    ) {
      try {
        return await flowiseClient.value.createPrediction({
          chatflowId: chatflowId,
          question: chatMessage,
          streaming: true,
          chatId: sessionId.value,
          overrideConfig: {
            analytics: {
              langFuse: {
                userId: UserDetails.value?.EmailAddress,
              },
            },
          },
        });
      } catch (err) {
        isWaitingLoader.value = false;
        throw err;
      }
    }

    function customMessageProcess() {
      if (
        CustomMessage.value.message
          ?.toLocaleLowerCase()
          ?.includes("uspsadrsvalid")
      ) {
        uspsAddress.value = CustomMessage.value.message?.replace(
          "USPSADRSVALID:",
          ""
        );
        enableValidateUSPSAddress();
      } else if (
        CustomMessage.value.message
          ?.toLocaleLowerCase()
          ?.includes("returnstatuscheck")
      ) {
        const data = CustomMessage.value.message?.replace(
          "ReturnStatusCheck:",
          ""
        );
        enableCheckReturnStatus();
        if (data) {
          const returnDetail = isValidJSONString(data) ? JSON.parse(data) : "";
          if (returnDetail) {
            returnStatus.BusinessEIN = returnDetail.BusinessEIN;
            returnStatus.RecipientName = returnDetail.RecipientName;
            returnStatus.LastFourDigitRecipientTIN =
              returnDetail.LastFourDigitRecipientTIN;
          }
        }
      } else {
        chatMessage.value = CustomMessage.value.message;
        flowiseStreaming();
      }
      CustomMessage.value = {};
    }

    async function generatePDF() {
      const id = isLiveAgent.value ? "livechat-to-print" : "aichat-to-print";
      var element = document.getElementById(id) as HTMLElement;

      const pageName = UserDetails.value?.PageUrl?.split("/")[3] || "Home";
      const initialHTML = `
     <div  class="page-break-inside-avoid pdfHeading" style="page-break-inside: avoid; margin-bottom: 20px;">
  <h1 style="font-size: 24px; margin-bottom: 10px; color: #333;">History</h1>
  
  <div class="custom-section" style="background-color: #f4f4f4; padding: 15px; border-radius: 8px; border: 1px solid #ddd;">
    <p style="font-size: 14px; color: #333; margin: 5px 0;">Product: <strong>${
      isTaxBandits.value ? "TaxBandits" : aiChatBotKeys.value?.ProductName
    }</strong></p>
    <p style="font-size: 14px; color: #333; margin: 5px 0;">Page: <strong> ${pageName}</strong></p>
  </div>
</div>
  `;
      if (
        !isLiveAgent.value ||
        (isLiveAgent.value && LiveAgentChatMessageDetails.value.length == 0)
      ) {
        element.querySelector(".greeting").style.visibility = "hidden";
      }

      // Prepend the new content at the beginning
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = initialHTML;
      element?.insertBefore(tempDiv.firstElementChild, element.firstChild);

      const opt = {
        margin: 1,
        filename: "chat-export.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 3 },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
        pagebreak: {
          mode: ["avoid-all", "css", "legacy"],
          avoid: ".page-break-inside-avoid",
        },
      };
      try {
        await html2pdf().set(opt).from(element).save();
        if (
          !isLiveAgent.value ||
          (isLiveAgent.value && LiveAgentChatMessageDetails.value.length == 0)
        ) {
          element.querySelector(".greeting").style.visibility = "visible";
        }
        element?.querySelector(".pdfHeading")?.remove();
      } catch (error) {
        console.error("PDF export failed:", error);
      }
      //  const chatContent = ChatMessageDetails.value.filter((chat)=>chat.color !='Yellow' && !chat.isWelcomeMessage && !IsNullorWhiteSpace(chat.text))
      //   const doc = new jsPDF()
      //   let yPosition = 20
      //   const lineHeight = 10
      //   const pageWidth = doc.internal.pageSize.width
      //   const margin = 20
      //   const maxLineWidth = pageWidth - 2 * margin
      //   chatContent.forEach((chat) => {
      //     // Add sender
      //     doc.setFont('helvetica', 'bold')
      //     doc.text(margin, yPosition, chat.senderType)
      //     yPosition += lineHeight
      //     // Add message with word wrap
      //     doc.setFont('helvetica', 'normal')
      //     const splitMessage = doc.splitTextToSize(chat.text, maxLineWidth)
      //     doc.text(margin, yPosition, splitMessage)
      //     yPosition += lineHeight * splitMessage.length
      //     // // Add timestamp
      //     // doc.setFont('helvetica', 'italic')
      //     // doc.setFontSize(10)
      //     // doc.text(margin, yPosition, chat.createdAt)
      //     // doc.setFontSize(12)
      //     yPosition += lineHeight * 1.5
      //     // Add new page if needed
      //     if (yPosition > doc.internal.pageSize.height - 20) {
      //       doc.addPage()
      //       yPosition = 20
      //     }
      //   })
      //   doc.save('chat-export.pdf')
    }

    function capitalizeFirstWord(text) {
      if (!text) return "";
      const words = text.split("");
      words[0] = words[0].toUpperCase();
      return words.join("");
    }

    const hasEnableExportOption = computed(() => {
      return isLiveAgent.value
        ? LiveAgentChatMessageDetails.value.filter(
            (data) => data.color != "Yellow"
          ).length > 0
        : ChatMessageDetails.value.filter(
            (data) => data.color != "Yellow" && !data.isWelcomeMessage
          ).length > 0 || oldChatConversationDetails.value.length > 0;
    });

    const isAIChatEmpty = computed(
      () =>
        ChatMessageDetails.value.filter(
          (data: Record<string, string>) =>
            data.color != "Yellow" && !data.isWelcomeMessage
        ).length == 0
    );

    //Convert XLSX file to separate CSV or Convert ZIP to Pdf
    async function extractDocument(type: string = "xlsx") {
      type = type?.includes("zip") ? type?.replace(".", "") : "sheet";
      excelDocument.value = excelDocument.value?.filter(
        (data) => data?.type.includes(type) && data.size <= 30 * 1024 * 1024
      );
      if (excelDocument.value.length > 0) {
        const dataURL = await readFileAsDataURL(excelDocument.value[0]);
        let [mimeType, base64Data] = dataURL?.split(",");
        mimeType = mimeType.split(":")[1].split(";")[0];
        if (!IsNullorWhiteSpace(base64Data)) {
          isUploadButtonLoading.value = true;
          const attachments = excelDocument.value?.map(
            (document: Record<string, string>) => {
              const url = useObjectUrl(document);
              return {
                FileType: document.type,
                FileSize: document.size,
                DataUrl: url.value,
              };
            }
          );

          const extractDocumentRequest = {
            Base64Data: base64Data,
            FileName: excelDocument.value[0]?.name,
            ConversionType: type == "zip" ? "ZipToPdf" : "ExcelToCSV",
          };
          spanSupportApiServices
            .postMethod(ExtractDocument, extractDocumentRequest)
            .then((res) => {
              isUploadButtonLoading.value = false;
              if (
                !IsNullorWhiteSpace(res) &&
                res.OperationStatus == Status.Success
              ) {
                excelDocument.value = [];
                ChatMessageDetails.value.forEach((data, index) => {
                  if (data?.isShowFileUpload) {
                    ChatMessageDetails.value.splice(index, 1);
                  }
                });
                ChatMessageDetails.value.push({
                  text: `Your ${type == "zip" ? "ZIP" : "Excel"} file`,
                  createdAt: moment(),
                  senderType: SenderType.Contact,
                  receiverType: SenderType.Bot,
                  messageId: uuid.v4(),
                  attachments: attachments
                });
                ChatMessageDetails.value[ ChatMessageDetails.value.length - 1].attachments.forEach((data: any) => {
                  data.DataUrl = res?.OriginalFileURL || res?.FileURL;
                });

                const fileUrls = res?.ExtractedFiles?.map((data) => {
                  return {
                    DataUrl: data?.DownloadURL,
                    SheetName: data?.FileName,
                  };
                });

                ChatMessageDetails.value.push({
                  text:
                    type == "zip"
                      ? 'Your extracted files are ready for download. Click "Download" to save the extracted file.'
                      : "CSV files converted",
                  createdAt: moment(),
                  senderType: SenderType.Bot,
                  receiverType: SenderType.Contact,
                  messageId: uuid.v4(),
                  attachments: fileUrls,
                  isShowAnotherFileUpload: true,
                  type: type
                });
                saveChatConversation();
              } else {
                isShowAlertBox.value = true;
                alertDetail.Status = Status.Failure;
                alertDetail.Message =
                  res?.ErrorMsg || "Cannot Process this file";
              }
            })
            .catch((err) => {
              isUploadButtonLoading.value = false;
            });
        }
      }
    }

    // Only allow XLSX (Excel) sheets; remove any other formats.
    function handleFileUploadChange(type: string = "xlsx") {
      type = type?.includes("zip") ? type?.replace(".", "") : "sheet";
      var isMaximumSizeAlert = excelDocument.value.some(
        (data: Record<string, never>) => data.size >= 30 * 1024 * 1024
      );

      excelDocument.value = excelDocument.value?.filter(
        (data: Record<string, never>) => {
          return data?.type?.includes(type) && data.size <= 30 * 1024 * 1024;
        }
      );
      if (isMaximumSizeAlert) {
        isShowAlertBox.value = true;
        alertDetail.Status = Status.Failure;
        alertDetail.Message = "File size should be less than 30 MB";
      }
    }

    function extractFileName(url: string) {
      if (url) {
        const match = url?.match(/\/([^\/]+)\.(csv|xlsx|zip|pdf)/);
        return match ? decodeURIComponent(match[1]) : "";
      }
      return "";
    }

    function extractFileExtenstion(url: string, callFrom: string = "") {
      if (url) {
        const match = url?.split("?")[0]?.split("com")[1];
        return callFrom == "extension" ||
          url.includes("csv") ||
          url.includes("xlsx")
          ? match
            ? "." + match.split(".")[1]
            : ""
          : "";
      }
      return "";
    }

    const isShowTextField = computed(() => {
      return isLiveAgent.value
        ? oldLiveChatConversationDetails.value.length == 0
        : true;
    });

    function extractDocumentConverter(type: string = "xlsx") {
      // || (chat.isShowFileUpload && (!chat.type?.includes(type)))
      ChatMessageDetails.value = ChatMessageDetails.value.filter(
        (chat: Record<string, string>) => !chat.isShowFileUpload
      );
      scrollToBottom();
      ChatMessageDetails.value.push({
        text: `Please upload your ${
          type == "xlsx" ? "Excel" : "zip"
        } file here`,
        senderType: SenderType.Bot,
        receiverType: SenderType.Contact,
        createdAt: moment(),
        color: "Yellow",
        isShowFileUpload: true,
        type: `.${type}`,
      });
    }

    // const downloadAll = (attachments) => {
    //   // Add a small delay between downloads to prevent browser blocking
    //   attachments.forEach((links, index) => {
    //     setTimeout(() => {
    //       const link = document.createElement('a');
    //       link.href = links.DataUrl
    //       link.download =links.SheetName ;
    //       link.click();
    //     }, index+1 * 200);
    //   });
    // };

    function downloadSummaryPdf() {
      ChatMessageDetails.value = ChatMessageDetails.value.filter(
        (chat) => !chat.isShowSummaryPdf
      );
      scrollToBottom();
      ChatMessageDetails.value.push({
        text: "Please enter below details to generate summary PDF \n \n Incorrect information will result in an empty summary.",
        senderType: SenderType.Bot,
        receiverType: SenderType.Contact,
        createdAt: moment(),
        color: "Yellow",
        isShowSummaryPdf: true,
      });
    }

    function GenerateSummaryPdf() {
      isGeneratePdfButtonLoader.value = true;
      const generateSummaryPdfRequest = {
        JWTToken: UserDetails.value?.JwtToken,
        BusinessName: summaryFormPdf.value.BusinessName,
        FormId: summaryFormPdf.value.FormId,
        TaxYear: summaryFormPdf.value.TaxYear,
        FormType: "FORM1096",
        DownloadType: "Download",
        EIN: summaryFormPdf.value.EIN,
      };
      spanSupportApiServices
        .pdfPostMethod(GetSummaryPdf, generateSummaryPdfRequest)
        .then((res) => {
          isGeneratePdfButtonLoader.value = false;

          if (!IsNullorWhiteSpace(res) && res.size > 0) {
            ChatMessageDetails.value = ChatMessageDetails.value.filter(
              (chat) => !chat.isShowSummaryPdf
            );

            try {
              const formType = FormType.find(
                (data) => data.value == summaryFormPdf.value.FormId
              );
              const businessDetails = `Business Name: ${summaryFormPdf.value.BusinessName}, ${summaryFormPdf.value.TINTypes}: ${summaryFormPdf.value.EIN}, TaxYear: ${summaryFormPdf.value.TaxYear}, From Type: ${formType?.key}`;
              const isDownaloadSummaryPdf = ChatMessageDetails.value[
                ChatMessageDetails.value.length - 1
              ]?.text
                ?.replace(/\s/g, "")
                ?.toLowerCase()
                ?.includes("summarypdf");
              if (isDownaloadSummaryPdf) {
                ChatMessageDetails.value[
                  ChatMessageDetails.value.length - 1
                ].text += " " + businessDetails;
              } else {
                ChatMessageDetails.value.push({
                  text: businessDetails,
                  createdAt: moment(),
                  senderType: SenderType.Contact,
                  receiverType: SenderType.Bot,
                  messageId: uuid.v4(),
                });
              }

              const fileExtension = res.type.replace("application/", "");
              let FileName =
                generateSummaryPdfRequest?.BusinessName +
                "_Form1096_" +
                summaryFormPdf.value.TaxYear +
                "_";
              FileName += getRandomNumber() + "." + fileExtension;
              const pdfFile = new Blob([res], {
                type: res.type,
              });
              const attachments = [
                {
                  DataUrl: useObjectUrl(pdfFile).value,
                  SheetName: FileName,
                },
              ];
              ChatMessageDetails.value.push({
                text: "Your Summary Pdf (Note: Invalid details will result in an empty summary PDF.)",
                createdAt: moment(),
                senderType: SenderType.Bot,
                receiverType: SenderType.Contact,
                messageId: uuid.v4(),
                attachments: attachments,
              });
              saveAs(pdfFile, FileName);
              saveChatConversation();
              summaryFormPdf.value = new SummaryFormPdf();
              v$.value.$reset();
            } catch (e) {
              isGeneratePdfButtonLoader.value = false;
              //  console.log(e)
            }
          } else {
            isShowAlertBox.value = true;
            alertDetail.Status = Status.Failure;
            alertDetail.Message = "Give Correct Business Details";
          }
        })
        .catch((e) => {});
    }

    const rules = {
      summaryFormPdf: {
        BusinessName: {
          required: helpers.withMessage("Business name mandatory", required),
          alphaNumAndDotValidator: helpers.withMessage(
            alphaNumAndDotValidator.message,
            () => alphaNumAndDotValidator.validator()
          ),
        },
        FormId: {
          required: helpers.withMessage("Form type mandatory", required),
        },
        EIN: {
          required: helpers.withMessage(
            () => `${summaryFormPdf.value.TINTypes} mandatory`,
            required
          ),
          maxLength: helpers.withMessage(
            () =>
              `${summaryFormPdf.value.TINTypes} should be ${
                mask.value.replace(/-/g, "").length
              }digits`,
            (value) => {
              return value?.length == mask.value?.length;
            }
          ),
        },
        TINTypes: {
          required: helpers.withMessage("TIN Type mandatory", required),
        },
        TaxYear: {
          required: helpers.withMessage("Tax Year mandatory", required),
        },
      },
      returnStatus: {
        BusinessEIN: {
          required: helpers.withMessage(() => `EIN mandatory`, required),
          maxLength: helpers.withMessage(
            () => `EIN should be 9 digits`,
            (value) => {
              return value?.replace(/-/g, "")?.length == 9;
            }
          ),
        },
        RecipientName: {
          required: helpers.withMessage("Recipient name mandatory", required),
          alphaNumAndDotValidator: helpers.withMessage(
            alphaNumAndDotValidator.message,
            () => alphaNumAndDotValidator.validator()
          ),
        },
        LastFourDigitRecipientTIN: {
          required: helpers.withMessage("TIN mandatory", required),
          maxLength: helpers.withMessage(
            () => `TIN should be 4 digits`,
            (value) => {
              return value?.length == 4;
            }
          ),
        },
      },
    };

    const v$ = useVuelidate(
      rules,
      { summaryFormPdf, returnStatus },
      { $stopPropagation: true }
    );

    function generatePDFSubmit() {
      v$.value.summaryFormPdf.$validate();
      if (!v$.value.summaryFormPdf.$invalid) {
        GenerateSummaryPdf();
      }
    }

    function emitCloseAlertBox() {
      isShowAlertBox.value = false;
    }

    const mask = computed(() => {
      return summaryFormPdf.value.TINTypes == "EIN"
        ? "##-#######"
        : "###-###-###";
    });

    function getUserIdFromJWT() {
      const request = {
        jwt: UserDetails.value?.JwtToken,
      };
      spanSupportApiServices
        .getMethod(GetUserIdFromJWT, request)
        .then((res) => {
          if (!IsNullorWhiteSpace(res)) {
            userID.value = res;
            setUserID(userID.value);
          }
        })
        .catch((err) => {});
    }

    function captureSnap() {
      ChatCommandCommunication({ command: "CaptureSnap" });
    }

    watch(DomSnapBase64Image, () => {
      if (!IsNullorWhiteSpace(DomSnapBase64Image.value)) {
        convertBas64toFile();
      }
    });

    function convertBas64toFile(fileName = "SnapShot") {
      const base64Data = DomSnapBase64Image.value.replace(
        /^data:image\/\w+;base64,/,
        ""
      );

      // Decode Base64 string to binary data
      const binaryData = atob(base64Data);

      // Convert binary data to an array buffer
      const arrayBuffer = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        arrayBuffer[i] = binaryData.charCodeAt(i);
      }
      // Create a Blob from the ArrayBuffer
      const blob = new Blob([arrayBuffer], { type: "image/jpeg" });

      // Create a File from the Blob
      const file = new File([blob], fileName, { type: "image/jpeg" });

      //  return file;
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);

      processFiles(Array.from(dataTransfer.files));
    }

    function validateAddress() {
      isUSPSAddressButtonLoader.value = true;
      flowiseStreaming("USPSValidator");
    }

    function enableValidateUSPSAddress() {
      ChatMessageDetails.value = ChatMessageDetails.value.filter(
        (chat: Record<string, string>) => !chat.isUSPSValidator
      );
      scrollToBottom();
      ChatMessageDetails.value.push({
        text: "Please enter your address here",
        senderType: SenderType.Bot,
        receiverType: SenderType.Contact,
        createdAt: moment(),
        color: "Yellow",
        isUSPSValidator: true,
      });
    }
    watch(isMaximizeWidget, () => {
      let command = isMaximizeWidget.value
        ? "IncreaseMaxmizeChat"
        : "MaxmizeChat";
      changeChatSize(command);
    });

    function changeChatSize(command: string) {
      ChatCommandCommunication({ command: command });
    }

    function navigateToHome() {
      isMaximizeWidget.value = false;
      setTimeout(() => {
        ctx.emit("NavigateToHome");
      });
    }
    function changeToAnotherBot() {
      isEnableBot2.value = !isEnableBot2.value;
    }

    function triggerAIChat(Ques: string) {
      chatMessage.value = Ques;
      flowiseStreaming();
    }

    const isAIResponseProcessing = computed(
      () =>
        isWaitingLoader.value ||
        isStreamingProcessing.value ||
        isUploadButtonLoading.value ||
        isUSPSAddressButtonLoader.value ||
        isSendButtonLoader.value
    );
    const userAIChatCount = computed(
      () =>
        ChatMessageDetails.value.filter(
          (chat: Record<string, string>) =>
            chat.senderType == SenderType.Contact
        ).length
    );

    async function updateSecondaryAI() {
      const updateSecondaryAIRequest = {
        ActiveAI: "ANTHROPIC",
        ProductKey: UserDetails.value?.ProductKey,
      };

      spanSupportApiServices
        .postMethod(UpdateActiveAI, updateSecondaryAIRequest)
        .then((res) => {
          console.log(res);
        })
        .catch((e) => {});
    }

    function enableCheckReturnStatus() {
      ChatMessageDetails.value = ChatMessageDetails.value.filter(
        (chat) => !chat.isCheckReturnStatus
      );
      scrollToBottom();
      ChatMessageDetails.value.push({
        text: "Please enter below details to check return status.",
        senderType: SenderType.Bot,
        receiverType: SenderType.Contact,
        createdAt: moment(),
        color: "Yellow",
        isCheckReturnStatus: true,
      });
    }

    function checkReturnStatus() {
      v$.value.returnStatus.$validate();
      if (!v$.value.returnStatus.$invalid) {
        isCheckReturnStatusButtonLoader.value = true;
        flowiseStreaming("ReturnStatus");
      }
    }

    const isHideInitialPromptsQuestion = computed(() => {
      return ChatMessageDetails.value.some(
        (chat: Record<string, string>) =>
          chat?.isShowSummaryPdf ||
          chat?.isCheckReturnStatus ||
          chat?.isShowFileUpload
      );
    });

    function checkEmailAddressAvailable() {
      if (
        !!buttonMode.value &&
        IsNullorWhiteSpace(UserDetails.value?.EmailAddress)
      ) {
        stepperElement.value = 1;
        isOpenLogin.value = true;
      } else {
        isOpenLogin.value = false;
        stepperElement.value = 0;
        changeToAlternateChat();
      }
    }

    function continueChat() {
      isOpenLogin.value = false;
      isLiveAgent.value = true;
    }

    function checkDownloadUrlIsExpiredOrNot(url:string | URL)
    {
      url = new URL(url);
      const expireTime = Number(url.searchParams.get("Expires") || 0)
      const currentTime  = Math.floor(Date.now()/1000)
     return expireTime < currentTime;
    }

    function showExpiredAlert()
    {
      isShowAlertBox.value = true
      alertDetail.Status = Status.Failure
      alertDetail.Message = "The download link has expired. Please upload the file again."
    } 

   const getAIFeatureContents = computed(()=>{
      return {
        AIAgent: isTaxBandits.value || isTax990.value ?  `Meet ${isTaxBandits.value ? 'BanditAI' : `${aiChatBotKeys.value?.ProductName||""}AI`} for Quick Help!` : " AI Agent answers instantly", 
        LiveAgentNeeded: isTaxBandits.value  || isTax990.value  ? "Ask your questions, or get help from a live agent" : "Ask for the live agent if needed"
      }
   })

    return {
      ChatMessageDetails,
      chatMessage,
      isWaitingLoader,
      isRestrictToClick,
      isChatBotDialog,
      openDialog,
      sendMessageToLiveAgent,
      isLiveAgent,
      submit,
      isChangeToFlowiseAI,
      changeToFlowiseAI,
      NotificationCount,
      showLiveAgentMessage,
      closeDialog,
      stepperElement,
      LiveAgentChatMessageDetails,
      switchChatType,
      isEnableLiveAgent,
      aiChatBotKeys,
      changeToAlternateChat,
      oldChatConversationDetails,
      isAllowTypeFormatter,
      readAloud,
      copyMessage,
      copied,
      transcribedText,
      messageId,
      copy,
      isTypingOn,
      thumbsCommends,
      isPlaying,
      stop,
      speakMessageId,
      dropZoneRef,
      isOverDropZone,
      BotBatchIcon,
      selectFiles,
      handlePaste,
      emojiIndex,
      showEmoji,
      isShowEmojiPicker,
      emojiMartRef,
      removeFile,
      fileInput,
      handleFileInputChange,
      selectInputFiles,
      chatBotContainer,
      getImageBlobUrl,
      useObjectUrl,
      getFileName,
      previewFiles,
      showPreview,
      formatTime,
      isTypingCompleted,
      buttonMode,
      ChatLiveAgentDetails,
      IsNullorWhiteSpace,
      liveAgentList,
      filteredLiveAgentList,
      getYouTubeThumbnail,
      scrollToBottom,
      clearChat,
      newChat,
      isDisableSwitchIcon,
      UserDetails,
      BotIconPath,
      navigateToHistory,
      isLiveAgentTyping,
      isTaxBandits,
      endChat,
      isStreamingProcessing,
      moment,
      generatePDF,
      capitalizeFirstWord,
      hasEnableExportOption,
      excelDocument,
      isUploadButtonLoading,
      extractDocument,
      handleFileUploadChange,
      extractFileName,
      oldLiveChatConversationDetails,
      oldAIChatConversationDetails,
      isShowTextField,
      isSendButtonLoader,
      extractDocumentConverter,
      downloadRefs,
      summaryFormPdf,
      FormType,
      TaxYear,
      v$,
      generatePDFSubmit,
      downloadSummaryPdf,
      isGeneratePdfButtonLoader,
      TINTypes,
      isShowAlertBox,
      alertDetail,
      emitCloseAlertBox,
      mask,
      envInfo,
      captureSnap,
      extractFileExtenstion,
      isUSPSAddressButtonLoader,
      validateAddress,
      enableValidateUSPSAddress,
      uspsAddress,
      isMaximizeWidget,
      navigateToHome,
      changeToAnotherBot,
      isEnableBot2,
      followUpPrompts,
      triggerAIChat,
      isAIChatEmpty,
      isShowInitialQuestions,
      initialPromptsQuestion,
      isAIResponseProcessing,
      userAIChatCount,
      enableCheckReturnStatus,
      returnStatus,
      checkReturnStatus,
      isCheckReturnStatusButtonLoader,
      isHideInitialPromptsQuestion,
      userLoginRef,
      isOpenLogin,
      checkEmailAddressAvailable,
      continueChat,
      isAIChatEnabled,
      checkDownloadUrlIsExpiredOrNot,
      showExpiredAlert,
      getAIFeatureContents
    };
  },
});
